import React from "react";
import { Button, Tooltip } from "antd";
import * as OrderUtils from "@/utils/OrderUtils";
import OrderDTO from "@/dto/order/OrderDTO";
import usePrintWaybillClick from "@/scenes/OrderManagement/components/BottomOperationBar/hooks/usePrintWaybillClick";
import { OrderedListOutlined, PrinterFilled, SendOutlined } from "@ant-design/icons";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { useGlobalStore, useShippingAreaStore } from "@/store/hooks";
import { observer } from "mobx-react-lite";
import { useModalStore } from "@/hooks/Modal/hooks";
import ShipOneOrderModal from "../../../dialogs/ShipOneOrderDialog/ShipOneOrderModal";
import PrintPackingListModal from "../../../dialogs/PrintPackingListDialog/PrintPackingListModal";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { cloneDeep } from "lodash";
import { buildSingleShopOrderOriginCheckFunc } from "@/utils/ShopUtils";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import PackingListCainiaoPrinter from "@/services/print/packinglist/PackingListCainiaoPrinter";

interface SingleOrderOperationColumnProps {
    order: OrderDTO;

    tab: OrderTab;
}

const SingleOrderOperationColumn: React.FC<SingleOrderOperationColumnProps> = ({ order, tab }) => {
    // 待发货、挂起tab因为有发货图标宽度为95，其他为70
    const isPendingShipmentTab = tab === OrderTab.PENDING_SHIPMENT || tab === OrderTab.ON_HOLD;
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();
    const orderManagementStore = useOrderManagementStore();
    const shop = globalStore.shopMapping.get(order.shopId);
    const { shippingAreaMap } = useShippingAreaStore();
    const refresh = () => {
        orderManagementStore.fetchOrders(order.shopId, tab);
    };
    function onShipTaskDone() {
        orderManagementStore.fetchOrders(order.shopId, tab);
        orderManagementStore.getOrderListCount();
    }
    const { handlePrint: showPrintWaybillDialog } = usePrintWaybillClick({
        getSelectedOrders() {
            const newOrder = cloneDeep(order);
            // 如果用户没有部分勾选则默认全选所有有效的商品打印
            if (!OrderUtils.isAnyOrderItemSelected(newOrder)) {
                OrderUtils.selectAllAvailableOrderItem(newOrder);
            }

            return [newOrder];
        },
        onShipTaskDone,
        multiPlatformModalOrderOriginCheck: buildSingleShopOrderOriginCheckFunc(
            shop,
            orderManagementStore.supplierPartnershipsStore,
        ),
        onUpdateOrder(id, params) {
            orderManagementStore.table.updateOrder(id, params);
        },
        onRefreshOrders: refresh,
        onUpdateOrderSellerRemarks: orderManagementStore.table.updateSellerRemarks,
        onUpdateSupplierOrderRemarks: orderManagementStore.table.updateSupplierOrderRemarks,
        supplierPartnershipsStore: orderManagementStore.supplierPartnershipsStore,
    });

    return (
        <td className="single-order-operation-td">
            <Tooltip title="打印快递单">
                <Button
                    size="small"
                    type="primary"
                    onClick={async (e) => {
                        e.stopPropagation();

                        const { shippingAreaId, templateId } = orderManagementStore.table.form?.getFieldsValue() || {};
                        const shippingArea = shippingAreaMap.get(Number(shippingAreaId));

                        let defaultTemplateId = shippingArea?.bindingWaybillTemplate;

                        if (templateId) {
                            await globalStore.waybillPrintStore.ensureWaybillTemplates();
                            const waybillTemplate = globalStore.waybillPrintStore.waybillTemplates?.find(
                                ({ id }) => id === templateId,
                            );

                            if (waybillTemplate) {
                                defaultTemplateId = waybillTemplate.id;
                            }
                        }

                        showPrintWaybillDialog(defaultTemplateId);
                    }}
                    icon={<PrinterFilled />}
                    className="mr5"
                ></Button>
            </Tooltip>
            <Tooltip title="打印发货单">
                <Button
                    size="small"
                    className="ant-btn-success mr5"
                    onClick={async (e) => {
                        e.stopPropagation();

                        if (!PackingListCainiaoPrinter.isReady()) {
                            PackingListCainiaoPrinter.openDownloadPluginDialog();
                            return;
                        }

                        const newOrder = cloneDeep(order);
                        // 如果用户没有部分勾选则默认全选所有有效的商品打印
                        if (!OrderUtils.isAnyOrderItemSelected(newOrder)) {
                            OrderUtils.selectAllAvailableOrderItem(newOrder);
                        }

                        modalStore.openModal(PrintPackingListModal, {
                            orders: [newOrder],
                        });
                    }}
                    icon={<OrderedListOutlined />}
                ></Button>
            </Tooltip>
            {isPendingShipmentTab ? (
                <Tooltip title="发货">
                    <AutoLoadingButton
                        size="small"
                        className="ant-btn-warning2"
                        style={{ marginRight: 0 }}
                        onClick={async (e) => {
                            e.stopPropagation();

                            const newOrder = cloneDeep(order);
                            // 如果用户没有部分勾选则默认全选所有有效的商品打印
                            if (!OrderUtils.isAnyOrderItemSelected(newOrder)) {
                                OrderUtils.selectAllAvailableOrderItem(newOrder);
                            }

                            modalStore.openModal(ShipOneOrderModal, {
                                order: newOrder,
                                onShipTaskDone: refresh,
                            });
                        }}
                        icon={<SendOutlined className="text-white" />}
                    ></AutoLoadingButton>
                </Tooltip>
            ) : null}
        </td>
    );
};

export default observer(SingleOrderOperationColumn);
